import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { formatTime, LunchBreak } from '../../utils/timesheetTime';
import {
  BATCH_SELECT,
  DEFAULT_DAY_START,
  DEFAULT_LUNCH,
  DEFAULT_STANDARD_HOURS,
  TimesheetBatch,
  TimesheetEntry,
  TimesheetEntryInput,
  TimesheetEntryPatch
} from './config';
import { dayLunch } from './timesheetDerive';

type LifecycleRpc =
  | 'submit_timesheet_batch'
  | 'reopen_timesheet_batch'
  | 'approve_timesheet_batch'
  | 'unapprove_timesheet_batch';

/**
 * One employee's weeks and the selected week's entries, plus every write
 * the pages make. Used by the employee's own Timesheets page and by the
 * manager's All Timesheets page, so the two cannot fetch different shapes.
 *
 * Reads set `error`; writes THROW the Supabase error so the caller can put
 * the message where the user is looking (a cell's save dot, the editor, a
 * toast). The database's messages are written to be shown verbatim.
 */
export function useTimesheetBatch(employeeId: string | null, weekEndingDate: string) {
  const supabase = useSupabaseClient();

  const [batches, setBatches] = useState<TimesheetBatch[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batch, setBatch] = useState<TimesheetBatch | null>(null);
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [standardHours, setStandardHours] = useState<number>(DEFAULT_STANDARD_HOURS);
  const [defaultStart, setDefaultStart] = useState<string>(DEFAULT_DAY_START);
  const [lunch, setLunch] = useState<LunchBreak>(DEFAULT_LUNCH);
  const [error, setError] = useState<string | null>(null);

  // Which batch the page is on right now. A refetch that comes back for any
  // other batch (the user switched week or employee while it was in flight)
  // is dropped, so it cannot overwrite the current week's rows.
  const currentBatchId = useRef<number | null>(null);
  currentBatchId.current = batch?.id ?? null;
  // Tokens for the reads that decide which batch is current: only the latest
  // request for a week is allowed to set state.
  const batchRequest = useRef(0);

  // --- reads ---------------------------------------------------------------

  const refetchBatches = useCallback(async () => {
    if (!employeeId) return;
    setLoadingBatches(true);
    const { data, error: err } = await supabase
      .from('timesheet_batches')
      .select(BATCH_SELECT)
      .eq('employee_id', employeeId)
      .order('week_ending_date', { ascending: false });
    setLoadingBatches(false);
    if (err) {
      console.error('Error fetching timesheet batches:', err);
      setError(`Failed to fetch timesheet weeks: ${err.message}`);
      return;
    }
    setBatches((data || []) as TimesheetBatch[]);
  }, [supabase, employeeId]);

  const refetchStandardHours = useCallback(async () => {
    if (!employeeId) return;
    const { data } = await supabase
      .from('team_members')
      .select('standard_hours_per_day, default_start_time, lunch_start_time, lunch_minutes')
      .eq('id', employeeId)
      .single();
    if (data?.standard_hours_per_day) setStandardHours(Number(data.standard_hours_per_day));
    if (data?.default_start_time) setDefaultStart(formatTime(data.default_start_time));
    if (data?.lunch_start_time) setLunch({ start: formatTime(data.lunch_start_time), minutes: Number(data.lunch_minutes ?? 0) });
  }, [supabase, employeeId]);

  /** Read the selected week's batch row without touching state. */
  const fetchBatchRow = useCallback(async (): Promise<TimesheetBatch | null> => {
    if (!employeeId || !weekEndingDate) return null;
    const { data, error: err } = await supabase
      .from('timesheet_batches')
      .select(BATCH_SELECT)
      .eq('employee_id', employeeId)
      .eq('week_ending_date', weekEndingDate)
      .limit(1);
    if (err) {
      console.error('Error fetching timesheet batch:', err);
      setError(`Failed to fetch the timesheet week: ${err.message}`);
      return null;
    }
    return ((data || [])[0] as TimesheetBatch | undefined) ?? null;
  }, [supabase, employeeId, weekEndingDate]);

  /** Read a batch's entries without touching state. */
  const fetchEntryRows = useCallback(
    async (batchId: number): Promise<TimesheetEntry[] | null> => {
      const { data, error: err } = await supabase
        .from('timesheet_entries_with_projects')
        .select('*')
        .eq('batch_id', batchId)
        .order('date', { ascending: true });
      if (err) {
        console.error('Error fetching timesheet entries:', err);
        setError(`Failed to fetch timesheet entries: ${err.message}`);
        return null;
      }
      return (data || []) as TimesheetEntry[];
    },
    [supabase]
  );

  const applyBatch = useCallback((next: TimesheetBatch | null) => {
    setBatch(next);
    // Keep the week strip's status in step with the selected batch.
    if (next) setBatches((prev) => prev.map((b) => (b.id === next.id ? next : b)));
  }, []);

  const refetchBatch = useCallback(async (): Promise<TimesheetBatch | null> => {
    const token = ++batchRequest.current;
    const next = await fetchBatchRow();
    if (token !== batchRequest.current) return next; // superseded by a later read
    applyBatch(next);
    return next;
  }, [fetchBatchRow, applyBatch]);

  const refetchEntries = useCallback(
    async (batchId?: number) => {
      const id = batchId ?? currentBatchId.current;
      if (!id) {
        setEntries([]);
        return;
      }
      const rows = await fetchEntryRows(id);
      // Dropped if the page has moved to another batch meanwhile.
      if (rows && currentBatchId.current === id) setEntries(rows);
    },
    [fetchEntryRows]
  );

  useEffect(() => {
    setError(null);
    setBatches([]);
    setBatch(null);
    setEntries([]);
    refetchBatches();
    refetchStandardHours();
  }, [refetchBatches, refetchStandardHours]);

  // Load the selected week: the batch and its entries are read first and
  // applied together, so the page never shows one week's header over another
  // week's rows. The token drops a load the user has already navigated past.
  useEffect(() => {
    const token = ++batchRequest.current;
    (async () => {
      setLoadingEntries(true);
      const next = await fetchBatchRow();
      const rows = next ? await fetchEntryRows(next.id) : [];
      if (token !== batchRequest.current) return;
      applyBatch(next);
      setEntries(rows ?? []);
      setLoadingEntries(false);
    })();
  }, [fetchBatchRow, fetchEntryRows, applyBatch]);

  // --- writes --------------------------------------------------------------

  const createEntry = useCallback(
    async (input: TimesheetEntryInput): Promise<{ id: number }> => {
      if (!batch) throw new Error('No timesheet week is selected.');
      const { data, error: err } = await supabase
        .from('timesheet_entries')
        .insert({ ...input, batch_id: batch.id })
        .select('id')
        .single();
      if (err) throw err;
      await refetchEntries(batch.id);
      return data as { id: number };
    },
    [supabase, batch, refetchEntries]
  );

  const updateEntry = useCallback(
    async (entryId: number, patch: TimesheetEntryPatch): Promise<void> => {
      const { error: err } = await supabase.from('timesheet_entries').update(patch).eq('id', entryId);
      if (err) throw err;
      await refetchEntries();
    },
    [supabase, refetchEntries]
  );

  const deleteEntry = useCallback(
    async (entryId: number): Promise<void> => {
      const { error: err } = await supabase.from('timesheet_entries').delete().eq('id', entryId);
      if (err) throw err;
      await refetchEntries();
    },
    [supabase, refetchEntries]
  );

  /**
   * Set the lunch break actually taken on one day of the open week, or pass
   * null to go back to the employee's usual break. A lunch identical to the
   * usual one is stored as no row at all, so a day only has its own lunch
   * while it differs. The database re-derives that day's timed hours
   * (recalculate_timesheet_day_hours), and the refetch brings them back
   * together with the lunch.
   */
  const setDayLunch = useCallback(
    async (date: string, next: LunchBreak | null): Promise<void> => {
      if (!batch) throw new Error('No timesheet week is selected.');
      const usual = next !== null && formatTime(next.start) === formatTime(lunch.start) && next.minutes === lunch.minutes;
      const { error: err } =
        next === null || usual
          ? await supabase.from('timesheet_day_lunches').delete().eq('batch_id', batch.id).eq('date', date)
          : await supabase
              .from('timesheet_day_lunches')
              .upsert({ batch_id: batch.id, date, lunch_start_time: next.start, lunch_minutes: next.minutes });
      if (err) throw err;
      await refetchEntries(batch.id);
    },
    [supabase, batch, lunch, refetchEntries]
  );

  /** The lunch break that applies on a day of the open week. */
  const lunchFor = useCallback((date: string): LunchBreak => dayLunch(entries, date, lunch).lunch, [entries, lunch]);

  const runLifecycle = useCallback(
    async (rpc: LifecycleRpc): Promise<void> => {
      if (!batch) throw new Error('No timesheet week is selected.');
      const { error: err } = await supabase.rpc(rpc, { p_batch_id: batch.id });
      if (err) throw err;
      const next = await refetchBatch();
      await refetchEntries(next?.id ?? batch.id);
    },
    [supabase, batch, refetchBatch, refetchEntries]
  );

  const submit = useCallback(() => runLifecycle('submit_timesheet_batch'), [runLifecycle]);
  const reopen = useCallback(() => runLifecycle('reopen_timesheet_batch'), [runLifecycle]);
  const approve = useCallback(() => runLifecycle('approve_timesheet_batch'), [runLifecycle]);
  const unapprove = useCallback(() => runLifecycle('unapprove_timesheet_batch'), [runLifecycle]);

  return {
    batches,
    loadingBatches,
    batch,
    entries,
    loadingEntries,
    standardHours,
    defaultStart,
    lunch,
    lunchFor,
    error,
    refetchBatches,
    refetchBatch,
    refetchEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    setDayLunch,
    submit,
    reopen,
    approve,
    unapprove
  };
}

export type TimesheetBatchApi = ReturnType<typeof useTimesheetBatch>;
